import React, { useEffect, useState, useMemo } from "react";
import { Circle, MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { InputPicker, Form, InputGroup, InputNumber, Button, TagPicker } from "rsuite";
import ReactModalWrap from "../common/ReactModalWrap";

import { useAddressAutocomplete } from "../hooks/useAddressAutocomplete";
import { useUserAutocomplete } from "../hooks/useUserAutocomplete";
import styles from "./Modal.module.scss";
import "rsuite/dist/rsuite.min.css";
import NetworkService from "../../NetworkService";

const MIN_RADIUS = 100;
const MAX_RADIUS = 100000;
const RADIUS_STEP = 100;
const DEFAULT_RADIUS = 400;
const DEFAULT_CENTER = { lat: 44.8155367, lng: 20.461178 };

const initFormData = {
  address: "",
  lat: null,
  lng: null,
  radius: DEFAULT_RADIUS,
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const MapClickHandler = ({ onMapClick, disabled }) => {
  useMapEvents({
    click(e) {
      if (!disabled && onMapClick) {
        onMapClick(e.latlng);
      }
    },
  });
  return null;
};

export default function AllowedAddressModal({ open, close, initialAddress, onSave }) {
  const initialAddressData = useMemo(() => {
    return {
      address: initialAddress?.address || "",
      lat: initialAddress?.lat || null,
      lng: initialAddress?.lng || null,
      radius: initialAddress?.radius || DEFAULT_RADIUS,
      label: initialAddress?.address || "",
      groupLabel: "Current",
      value: initialAddress?.lat && initialAddress?.lng ? `${initialAddress.lat},${initialAddress.lng}` : null,
    };
  }, [initialAddress]);

  const isEdit = Boolean(initialAddress);

  const [searchValue, setSearchValue] = useState("");
  const hasSearch = searchValue.trim().length > 0;

  const clearSearch = () => {
    setFormData(initFormData);
  };
  const handleSearch = (val) => {
    setSearchValue(val);
    onSearch(val);
  };

  const [companyAddresses, setCompanyAddresses] = useState([]);

  console.log(initialAddress);

  const [formData, setFormData] = useState(initFormData);
  const [selectedUserOptions, setSelectedUserOptions] = useState([]);

  const { address, lat, lng, radius } = formData;

  useEffect(() => {
    setFormData(initialAddressData);
  }, [initialAddressData]);

  const { options, onSearch, isLoading } = useAddressAutocomplete();
  const { options: userOptions, onSearch: onUserSearch, isLoading: isUserLoading } = useUserAutocomplete();

  const combinedUserOptions = useMemo(() => {
    const map = new Map();
    selectedUserOptions.forEach((opt) => map.set(opt.value, opt));
    userOptions.forEach((opt) => map.set(opt.value, opt));
    return Array.from(map.values());
  }, [userOptions, selectedUserOptions]);

  const userPickerData = useMemo(() => {
    return userOptions.filter((opt) => !selectedUserOptions.some((s) => s.value === opt.value));
  }, [userOptions, selectedUserOptions]);

  const allOptions = useMemo(() => {
    const initialOption = initialAddressData.lat && initialAddressData.lng ? [initialAddressData] : [];
    const currentOption = (lat && lng && (lat !== initialAddressData.lat || lng !== initialAddressData.lng)) ? [{
      label: address,
      value: `${lat},${lng}`,
      lat: Number(lat),
      lng: Number(lng),
      groupLabel: "Selected from map"
    }] : [];

    const filteredAlreadyUsed = hasSearch
      ? companyAddresses
          .filter((addr) => addr.address.toLowerCase().includes(searchValue.toLowerCase()))
          .map((addr) => ({
            label: addr.address,
            value: `${addr.lat},${addr.lng}`,
            lat: Number(addr.lat),
            lng: Number(addr.lng),
            groupLabel: "Already used",
          }))
          .slice(0, 5)
      : [];

    return [...initialOption, ...currentOption, ...filteredAlreadyUsed, ...options];
  }, [options, companyAddresses, searchValue, hasSearch, initialAddressData, lat, lng, address]);

  useEffect(() => {
    if (open) {
      NetworkService.getCompanyAddresses().then((res) => {
        setCompanyAddresses(res.data || []);
      });
    }
  }, [open]);

  const handleSave = () => {
    if (!onSave) return;
    const payload = {
      ...initialAddress,
      address: address.trim(),
      lat: lat,
      lng: lng,
      radius,
      apply_to_users: selectedUserOptions.map((opt) => opt.value),
    };
    onSave(payload, { isEdit });
  };

  const handleSelect = (val, item) => {
    if (!item) return;
    setFormData({
      address: item.label,
      lat: item.lat,
      lng: item.lng,
      radius,
    });
  };

  const handleLocationUpdate = (latlng) => {
    NetworkService.reverseGeocode(latlng.lat, latlng.lng)
      .then((res) => {
        const dataObj = Array.isArray(res) ? res[0] : (res?.data || res);
        const addressName = dataObj?.formattedAddress || dataObj?.display_name || "";
        
        if (addressName) {
          setFormData((prev) => ({
            ...prev,
            lat: latlng.lat,
            lng: latlng.lng,
            address: addressName,
          }));
        }
      })
      .catch((err) => console.log(err));
  };

  const handleMapClick = (latlng) => {
    handleLocationUpdate(latlng);
  };

  const handleMarkerDragEnd = (e) => {
    const latlng = e.target.getLatLng();
    handleLocationUpdate(latlng);
  };

  const center = lat && lng ? [lat, lng] : DEFAULT_CENTER;
  const isSaveDisabled = !address.trim() || !lat || !lng;
  const pickerValue = lat && lng ? `${lat},${lng}` : null;

  return (
    <ReactModalWrap isOpen={open} close={close} customStyle={{ content: { maxWidth: "95vw" } }}>
      <div className={styles.content} style={{ paddingTop: 20 }}>
        <div style={{ height: 250, width: "100%" }}>
          <MapContainer
            attributionControl={false}
            center={center}
            zoom={12}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom={false}>
            <TileLayer url="https://basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}.png?key=cb1_2g08_1_e9d474a7cdba96691b28ac2c" />
            <FitBounds startPoint={[lat, lng]} />
            <MapClickHandler onMapClick={handleMapClick} disabled={Boolean(lat && lng)} />
            {lat && lng && (
              <Marker
                position={[lat, lng]}
                draggable={true}
                eventHandlers={{
                  dragend: handleMarkerDragEnd,
                }}
              />
            )}
            {radius > 0 && lat && lng && <Circle center={center} radius={radius} pathOptions={{ color: "#3BD4AE" }} />}
          </MapContainer>
        </div>
        <div className={styles.header} style={{ textAlign: "left" }}>
          {isEdit ? "Edit address" : "Set address"}
        </div>
        <Form fluid formValue={formData} onSubmit={handleSave}>
          <div className={styles.formContainer}>
            <Form.Group style={{ flex: 2, width: "100%" }} controlId="address" className={styles.formGroup}>
              <Form.ControlLabel className={styles.label}>Address: </Form.ControlLabel>
              <Form.Control
                block
                appearance="subtle"
                accepter={InputPicker}
                size="lg"
                className={styles.inputPickerClass}
                menuStyle={{ zIndex: 99999999 }}
                placeholder="Search address..."
                data={allOptions}
                value={pickerValue}
                defaultValue={address}
                loading={isLoading}
                onSearch={handleSearch}
                onSelect={handleSelect}
                onClean={clearSearch}
                groupBy="groupLabel"
                cleanable
                searchBy={() => true}
                searchable
              />
            </Form.Group>

            <Form.Group style={{ flex: 1 }} controlId="radius" className={styles.formGroup}>
              <Form.ControlLabel className={styles.label}>Set a range limit:</Form.ControlLabel>
              <InputGroup
                size="lg"
                style={{ width: "200px", padding: "2px 5px", gap: "5px", background: "#FFF", alignItems: "center" }}>
                <Button
                  style={{ width: "30px", height: "30px", borderRadius: "100%" }}
                  className={styles.buttonMinus}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setFormData((prev) => ({
                      ...prev,
                      radius: clamp(prev.radius - RADIUS_STEP, MIN_RADIUS, MAX_RADIUS),
                    }));
                  }}>
                  -
                </Button>
                <InputNumber size="md" className={styles.inputNumberNoSpinner} readOnly value={radius} />
                <Button
                  style={{ width: "30px", height: "30px", borderRadius: "100%" }}
                  className={styles.buttonPlus}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setFormData((prev) => ({
                      ...prev,
                      radius: clamp(prev.radius + RADIUS_STEP, MIN_RADIUS, MAX_RADIUS),
                    }));
                  }}>
                  +
                </Button>
              </InputGroup>
            </Form.Group>
          </div>

          {!initialAddress && (
            <Form.Group>
              <Form.ControlLabel className={styles.label}>Apply to more users</Form.ControlLabel>
              <Form.Control
                block
                appearance="subtle"
                accepter={TagPicker}
                size="lg"
                className={styles.inputPickerClass}
                menuStyle={{ zIndex: 99999999 }}
                placeholder="Search users..."
                data={userPickerData}
                cacheData={combinedUserOptions}
                value={selectedUserOptions.map((opt) => opt.value)}
                onChange={(values) => {
                  const selected = values
                    .map((v) => combinedUserOptions.find((opt) => opt.value === v))
                    .filter(Boolean);
                  setSelectedUserOptions(selected);
                }}
                loading={isUserLoading}
                onSearch={onUserSearch}
                onClean={() => setSelectedUserOptions([])}
                cleanable
                searchBy={() => true}
                searchable
              />
            </Form.Group>
          )}

          <Button type="submit" className={styles.userSubmit} appearance="primary" disabled={isSaveDisabled}>
            Save changes
          </Button>
        </Form>
      </div>
    </ReactModalWrap>
  );
}

const FitBounds = ({ startPoint, polyline }) => {
  const recenter = (startPoint, polyline) => {
    if (startPoint && startPoint[0]) {
      let bounds = window.L.latLngBounds([startPoint]);
      if (polyline) {
        bounds.extend(polyline);
      }
      map.flyToBounds(bounds, {
        maxZoom: 15,
        padding: [20, 20],
        animate: false,
      });
    }
  };

  const map = useMap();
  useEffect(() => {
    recenter(startPoint, polyline);
  }, [startPoint, polyline, map]);

  return null;
};
